import sodium from 'libsodium-wrappers';
import type { KeyExchangeRequest, KeyExchangeResponse, EncryptedFrame, EncryptedMessage, InputCommand } from '../types';

export type EncryptionState = 'none' | 'negotiating' | 'established' | 'failed';

export interface EncryptionServiceEvents {
    onStateChanged?: (state: EncryptionState) => void;
}

/**
 * E2E encryption service matching C# EncryptionService + SecureSessionManager protocol.
 *
 * Key exchange: X25519 (Curve25519 ECDH)
 * Encryption: XChaCha20-Poly1305 (AEAD)
 * Key derivation: GenericHash(sharedSecret + "LEVOR-RemoteDesktop-E2E-v1") → 32 bytes
 * Wire format: nonce (24 bytes) prepended to ciphertext, base64-encoded for SignalR JSON
 */
export class EncryptionService {
    private publicKey: Uint8Array | null = null;
    private privateKey: Uint8Array | null = null;
    private sharedSecret: Uint8Array | null = null;
    private localNonce: string | null = null;
    private sendSequence = 0;
    private _state: EncryptionState = 'none';
    private ready = false;
    private events: EncryptionServiceEvents;

    private static readonly DOMAIN_CONTEXT = 'LEVOR-RemoteDesktop-E2E-v1';

    get state(): EncryptionState {
        return this._state;
    }

    get isSecure(): boolean {
        return this._state === 'established';
    }

    constructor(events: EncryptionServiceEvents = {}) {
        this.events = events;
    }

    /**
     * Must be called once before any other method. Loads libsodium WASM.
     */
    async ensureReady(): Promise<void> {
        if (this.ready) return;
        await sodium.ready;
        this.ready = true;
    }

    /**
     * Initialize a new X25519 keypair for this session.
     */
    async initialize(): Promise<void> {
        await this.ensureReady();
        const kp = sodium.crypto_box_keypair();
        this.publicKey = kp.publicKey;
        this.privateKey = kp.privateKey;
        this.sharedSecret = null;
        this.sendSequence = 0;
        this.setState('none');
    }

    /**
     * Create a key exchange request (initiator role).
     * Matches C# SecureSessionManager.CreateKeyExchangeRequest().
     */
    createKeyExchangeRequest(): KeyExchangeRequest {
        if (!this.publicKey) {
            throw new Error('EncryptionService not initialized');
        }

        this.localNonce = sodium.to_base64(
            sodium.randombytes_buf(16),
            sodium.base64_variants.ORIGINAL
        );
        this.setState('negotiating');

        return {
            publicKey: sodium.to_base64(this.publicKey, sodium.base64_variants.ORIGINAL),
            nonce: this.localNonce,
            timestamp: Date.now(),
            version: 1,
        };
    }

    /**
     * Process a key exchange response from the agent (initiator role).
     * Matches C# SecureSessionManager.ProcessKeyExchangeResponse().
     */
    processKeyExchangeResponse(response: KeyExchangeResponse): boolean {
        if (this._state !== 'negotiating') {
            console.warn('[Encryption] processKeyExchangeResponse called but not in negotiating state');
            return false;
        }

        if (!response.accepted) {
            console.warn('[Encryption] Key exchange rejected:', response.errorMessage);
            this.setState('failed');
            return false;
        }

        // Verify our nonce was echoed back
        if (response.requestNonce !== this.localNonce) {
            console.warn('[Encryption] Nonce mismatch in key exchange response');
            this.setState('failed');
            return false;
        }

        try {
            const remotePublicKey = sodium.from_base64(
                response.publicKey,
                sodium.base64_variants.ORIGINAL
            );

            if (!this.completeKeyExchange(remotePublicKey)) {
                this.setState('failed');
                return false;
            }

            this.setState('established');
            return true;
        } catch (err) {
            console.error('[Encryption] Key exchange response processing failed:', err);
            this.setState('failed');
            return false;
        }
    }

    /**
     * Process a key exchange request (responder role — used if agent initiates).
     * Matches C# SecureSessionManager.ProcessKeyExchangeRequest().
     */
    processKeyExchangeRequest(request: KeyExchangeRequest): KeyExchangeResponse {
        if (!this.publicKey) {
            return this.createErrorResponse(request.nonce, 'EncryptionService not initialized');
        }

        // Check timestamp freshness (30s window)
        if (Math.abs(Date.now() - request.timestamp) > 30_000) {
            return this.createErrorResponse(request.nonce, 'Key exchange request expired');
        }

        try {
            const remotePublicKey = sodium.from_base64(
                request.publicKey,
                sodium.base64_variants.ORIGINAL
            );

            if (!this.completeKeyExchange(remotePublicKey)) {
                return this.createErrorResponse(request.nonce, 'Key exchange failed');
            }

            const responseNonce = sodium.to_base64(
                sodium.randombytes_buf(16),
                sodium.base64_variants.ORIGINAL
            );

            this.setState('established');

            return {
                publicKey: sodium.to_base64(this.publicKey, sodium.base64_variants.ORIGINAL),
                requestNonce: request.nonce,
                responseNonce,
                timestamp: Date.now(),
                accepted: true,
            };
        } catch (err) {
            this.setState('failed');
            return this.createErrorResponse(request.nonce, `Key exchange error: ${err}`);
        }
    }

    /**
     * Encrypt raw bytes. Returns nonce (24 bytes) + ciphertext as Uint8Array.
     */
    encrypt(plaintext: Uint8Array): Uint8Array {
        if (!this.sharedSecret) {
            throw new Error('Encryption not established');
        }

        const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
        const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
            plaintext,
            null, // no additional data
            null, // nsec (unused, must be null)
            nonce,
            this.sharedSecret
        );

        // Prepend nonce to ciphertext (matches C# format)
        const result = new Uint8Array(nonce.length + ciphertext.length);
        result.set(nonce, 0);
        result.set(ciphertext, nonce.length);
        return result;
    }

    /**
     * Decrypt data. Input is nonce (24 bytes) + ciphertext.
     */
    decrypt(encryptedData: Uint8Array): Uint8Array {
        if (!this.sharedSecret) {
            throw new Error('Encryption not established');
        }

        const nonceLength = sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES; // 24
        if (encryptedData.length < nonceLength + 16) {
            throw new Error('Invalid encrypted data (too short)');
        }

        const nonce = encryptedData.slice(0, nonceLength);
        const ciphertext = encryptedData.slice(nonceLength);

        return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
            null, // nsec (unused, must be null)
            ciphertext,
            null, // no additional data
            nonce,
            this.sharedSecret
        );
    }

    /**
     * Create an EncryptedFrame from raw frame bytes.
     * Matches C# SecureSessionManager.CreateEncryptedFrame().
     */
    createEncryptedFrame(
        frameData: Uint8Array,
        width: number,
        height: number,
        codec: number,
        isKeyFrame: boolean
    ): EncryptedFrame {
        const encrypted = this.encrypt(frameData);
        return {
            data: sodium.to_base64(encrypted, sodium.base64_variants.ORIGINAL),
            frameNumber: ++this.sendSequence,
            width,
            height,
            codec,
            isKeyFrame,
        };
    }

    /**
     * Decrypt an EncryptedFrame. Returns raw frame bytes.
     * Matches C# SecureSessionManager.DecryptEncryptedFrame().
     */
    decryptEncryptedFrame(frame: EncryptedFrame): Uint8Array {
        const encrypted = sodium.from_base64(frame.data, sodium.base64_variants.ORIGINAL);
        return this.decrypt(encrypted);
    }

    /**
     * Encrypt an input command for transmission.
     * Matches C# SecureSessionManager.EncryptMessage().
     */
    encryptInputCommand(command: InputCommand): EncryptedMessage {
        const json = JSON.stringify(command);
        const plaintext = sodium.from_string(json);
        const encrypted = this.encrypt(plaintext);

        return {
            type: 'InputCommand',
            payload: sodium.to_base64(encrypted, sodium.base64_variants.ORIGINAL),
            sequenceNumber: ++this.sendSequence,
            timestamp: Date.now(),
        };
    }

    /**
     * Reset all state (for session end / new session).
     */
    reset(): void {
        if (this.privateKey) {
            sodium.memzero(this.privateKey);
        }
        if (this.sharedSecret) {
            sodium.memzero(this.sharedSecret);
        }
        this.publicKey = null;
        this.privateKey = null;
        this.sharedSecret = null;
        this.localNonce = null;
        this.sendSequence = 0;
        this.setState('none');
    }

    // ---- Private ----

    /**
     * Derive shared secret using X25519 scalar multiplication + domain-separated KDF.
     * Matches C# EncryptionService.CompleteKeyExchange().
     */
    private completeKeyExchange(remotePublicKey: Uint8Array): boolean {
        if (!this.privateKey) return false;
        if (remotePublicKey.length !== 32) return false;

        try {
            // X25519 scalar multiplication (same as ScalarMult.Mult in C#)
            const rawSharedSecret = sodium.crypto_scalarmult(this.privateKey, remotePublicKey);

            // Domain-separated key derivation (matches C# exactly):
            // Hash(rawSharedSecret + "LEVOR-RemoteDesktop-E2E-v1") → 32 bytes
            const context = sodium.from_string(EncryptionService.DOMAIN_CONTEXT);
            const toHash = new Uint8Array(rawSharedSecret.length + context.length);
            toHash.set(rawSharedSecret, 0);
            toHash.set(context, rawSharedSecret.length);

            this.sharedSecret = sodium.crypto_generichash(32, toHash, null);

            // Clear intermediate values
            sodium.memzero(rawSharedSecret);
            sodium.memzero(toHash);

            return true;
        } catch (err) {
            console.error('[Encryption] Key exchange computation failed:', err);
            this.sharedSecret = null;
            return false;
        }
    }

    private setState(state: EncryptionState): void {
        this._state = state;
        this.events.onStateChanged?.(state);
    }

    private createErrorResponse(requestNonce: string, errorMessage: string): KeyExchangeResponse {
        return {
            publicKey: '',
            requestNonce: requestNonce || '',
            responseNonce: '',
            timestamp: Date.now(),
            accepted: false,
            errorMessage,
        };
    }
}
